import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Home, Building2, BedDouble, ChevronDown, LogOut, User, Plus, Pencil, Pause, Play, ClipboardList, Heart, Bookmark, Trash2, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, peso, priceLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { SiteFooter } from "@/components/SiteFooter";
import { propertyTypeLabel } from "@/lib/taxonomy";
import { BrandLogo } from "@/components/BrandLogo";

export function ConsumerHeader() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200" data-testid="consumer-header">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-[72px] sm:h-[84px] flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" aria-label="PropertyConnect home">
          <BrandLogo className="h-[66px] sm:h-[76px]" />
        </Link>
        <nav className="flex items-center gap-1 sm:gap-2">
          <Link to="/marketplace?type=For%20Sale" data-testid="consumer-nav-buy" className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100">Buy</Link>
          <Link to="/marketplace?type=For%20Rent" data-testid="consumer-nav-rent" className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100">Rent</Link>
          <Link to="/marketplace?type=Unit%20Hosting" data-testid="consumer-nav-stays" className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100">Stays</Link>
          <Link to="/sell" data-testid="consumer-nav-sell" className="px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100">Sell</Link>
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="consumer-nav-account" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 outline-none">
              <User className="h-4 w-4" /> Account <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-3 py-2 text-xs text-slate-500 border-b border-slate-100">{user?.name}<br /><span className="text-slate-400">{user?.email}</span></div>
              <DropdownMenuItem data-testid="consumer-signout" onClick={async () => { await logout(); nav("/login"); }} className="text-red-600 cursor-pointer">
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
      </div>
    </header>
  );
}

export default function ConsumerHome() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [reqs, setReqs] = useState(null);
  const [savedProps, setSavedProps] = useState(null);
  const [savedSearches, setSavedSearches] = useState(null);
  const [inquiries, setInquiries] = useState(null);
  const [submissions, setSubmissions] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/me/requirements"); setReqs(data); } catch { setReqs([]); }
    try { const { data } = await api.get("/me/saved-properties"); setSavedProps(data); } catch { setSavedProps([]); }
    try { const { data } = await api.get("/me/saved-searches"); setSavedSearches(data); } catch { setSavedSearches([]); }
    try { const { data } = await api.get("/me/inquiries"); setInquiries(data); } catch { setInquiries([]); }
    try { const { data } = await api.get("/me/property-submissions"); setSubmissions(data); } catch { setSubmissions([]); }
  };
  useEffect(() => { load(); }, []);
  const toggle = async (r) => {
    const next = r.status === "Paused" ? "Active" : "Paused";
    try { await api.patch(`/me/requirements/${r.id}/status`, { status: next }); toast.success(next === "Paused" ? "Requirement paused" : "Requirement reactivated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const unsaveProperty = async (pid) => {
    try { await api.delete(`/me/saved-properties/${pid}`); setSavedProps((s) => (s || []).filter((x) => x.id !== pid)); toast.success("Removed from saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const deleteSearch = async (sid) => {
    try { await api.delete(`/me/saved-searches/${sid}`); setSavedSearches((s) => (s || []).filter((x) => x.id !== sid)); toast.success("Saved search deleted"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const runSearch = (f) => {
    const q = new URLSearchParams();
    q.set("type", f.listing_type || "For Sale");
    if (f.location_code) q.set("location_code", f.location_code);
    if (f.city) q.set("loc", f.city);
    if (f.property_type) q.set("property_type", f.property_type);
    if (f.min_price != null) q.set("min_price", String(f.min_price));
    if (f.max_price != null) q.set("max_price", String(f.max_price));
    nav(`/marketplace?${q.toString()}`);
  };
  const searchSummary = (f) => {
    const bits = [];
    if (f.property_type) bits.push(f.property_type);
    const lo = f.min_price, hi = f.max_price;
    if (lo && hi) bits.push(`${peso(lo)}–${peso(hi)}`);
    else if (lo) bits.push(`from ${peso(lo)}`);
    else if (hi) bits.push(`up to ${peso(hi)}`);
    return bits.join(" · ") || "Any type · Any price";
  };
  const INQ_LABEL = { GENERAL: "General", INFORMATION: "Information", PRICE_PAYMENT: "Price & payment", VIEWING_REQUEST: "Viewing request", AVAILABILITY_REQUEST: "Availability request" };
  const INQ_STATUS = { NEW: "bg-emerald-50 text-emerald-600", CONTACTED: "bg-amber-50 text-amber-600", CLOSED: "bg-slate-100 text-slate-500" };
  const SUB_STATUS = { SUBMITTED: "bg-blue-50 text-blue-600", APPROVED: "bg-emerald-50 text-emerald-600", REJECTED: "bg-red-50 text-red-600" };
  const SUB_LABEL = { SUBMITTED: "Submitted", APPROVED: "Approved", REJECTED: "Rejected" };
  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="consumer-home">
      <ConsumerHeader />
      <div className="relative overflow-hidden" style={{ background: "#0F2C59" }}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-20 sm:py-28 relative z-10">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C79A3B]">Welcome to PropertyConnect</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white mt-3 max-w-2xl leading-tight">
            {user?.first_name ? `Hi ${user.first_name}, ` : ""}what would you like to do today?
          </h1>
          <p className="text-slate-300 mt-4 max-w-xl">Find a property, explore rentals and stays, or manage your property requirements and inquiries.</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button data-testid="consumer-find-property" onClick={() => nav("/marketplace")} className="h-12 px-7 rounded-xl bg-[#C79A3B] hover:bg-[#b6892f] text-white font-semibold text-base">
              <Search className="h-5 w-5 mr-2" /> Find a Property
            </Button>
          </div>
        </div>
      </div>
      <div className="flex-1 w-full max-w-[1400px] mx-auto px-4 sm:px-6 py-12">
        <div className="grid sm:grid-cols-3 gap-5 max-w-4xl">
          <button data-testid="consumer-tile-buy" onClick={() => nav("/marketplace?type=For%20Sale")} className="text-left bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#0F2C59] hover:shadow-md transition-all">
            <div className="h-11 w-11 rounded-xl bg-[#C79A3B]/10 flex items-center justify-center"><Home className="h-6 w-6 text-[#C79A3B]" /></div>
            <h3 className="font-display text-lg font-bold text-slate-900 mt-4">Buy a property</h3>
            <p className="text-sm text-slate-500 mt-1">Explore homes, condos and lots for sale.</p>
          </button>
          <button data-testid="consumer-tile-rent" onClick={() => nav("/marketplace?type=For%20Rent")} className="text-left bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#0F2C59] hover:shadow-md transition-all">
            <div className="h-11 w-11 rounded-xl bg-[#0F2C59]/10 flex items-center justify-center"><Building2 className="h-6 w-6 text-[#0F2C59]" /></div>
            <h3 className="font-display text-lg font-bold text-slate-900 mt-4">Rent a property</h3>
            <p className="text-sm text-slate-500 mt-1">Find rentals that match your budget and location.</p>
          </button>
          <button data-testid="consumer-tile-stays" onClick={() => nav("/marketplace?type=Unit%20Hosting")} className="text-left bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#0F2C59] hover:shadow-md transition-all">
            <div className="h-11 w-11 rounded-xl bg-[#C79A3B]/10 flex items-center justify-center"><BedDouble className="h-6 w-6 text-[#C79A3B]" /></div>
            <h3 className="font-display text-lg font-bold text-slate-900 mt-4">Find a Stay</h3>
            <p className="text-sm text-slate-500 mt-1">Discover short-term stays and professionally hosted properties.</p>
          </button>
        </div>

        <div className="mt-14" data-testid="consumer-requirements">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-900">My Property Requirements</h2>
              <p className="text-sm text-slate-500 mt-1">Tell us what you’re looking for.</p>
            </div>
            <Button data-testid="req-create" onClick={() => nav("/requirements/new")} className="h-11 px-5 rounded-xl bg-accent text-accent-foreground font-semibold"><Plus className="h-4 w-4 mr-1" /> Create Requirement</Button>
          </div>
          {reqs === null ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : reqs.length === 0 ? (
            <div data-testid="req-empty" className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center">
              <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3"><ClipboardList className="h-6 w-6 text-slate-400" /></div>
              <p className="text-slate-600 font-medium">I’m looking for a property</p>
              <p className="text-sm text-slate-400 mt-1 mb-4">Create a Buy or Rent requirement to describe your ideal property.</p>
              <Button data-testid="req-create-empty" onClick={() => nav("/requirements/new")} className="h-10 px-5 rounded-xl bg-accent text-accent-foreground font-semibold"><Plus className="h-4 w-4 mr-1" /> Create Requirement</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {reqs.map((r) => {
                const rent = r.listing_type === "For Rent";
                const cities = r.cities || [];
                const loc = cities[0] ? (cities.length > 1 ? `${cities[0]} +${cities.length - 1} more` : cities[0]) : "Any location";
                const budget = (r.budget_min || r.budget_max) ? `${peso(r.budget_min || 0)} – ${peso(r.budget_max || 0)}` : "Any budget";
                const paused = r.status === "Paused";
                return (
                  <div key={r.id} data-testid={`req-card-${r.id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
                    <div className="flex items-center justify-between">
                      <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${rent ? "bg-[#0F2C59] text-white" : "bg-[#C79A3B] text-white"}`}>{rent ? "Rent" : "Buy"}</span>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${paused ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-600"}`}>{paused ? "Paused" : "Active"}</span>
                    </div>
                    <h3 className="font-semibold text-slate-900 mt-3">{loc}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{r.property_type === "Any" ? "Any / Flexible" : propertyTypeLabel(r.property_type)}</p>
                    <p className="text-sm font-display font-bold text-slate-800 mt-2">{budget}</p>
                    <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                      {!paused && <Button data-testid={`req-matches-${r.id}`} onClick={() => nav(`/requirements/${r.id}/matches`)} className="flex-1 h-9 rounded-lg text-sm bg-accent text-accent-foreground font-semibold">View Matches</Button>}
                      <Button data-testid={`req-edit-${r.id}`} variant="outline" onClick={() => nav(`/requirements/${r.id}/edit`)} className="h-9 rounded-lg text-sm px-3"><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button data-testid={`req-toggle-${r.id}`} variant="outline" onClick={() => toggle(r)} className="h-9 rounded-lg text-sm px-3">{paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Saved Properties */}
        <div className="mt-14" data-testid="saved-properties-section">
          <div className="mb-5">
            <h2 className="font-display text-2xl font-bold text-slate-900">Saved Properties</h2>
            <p className="text-sm text-slate-500 mt-1">Listings you bookmarked from the marketplace.</p>
          </div>
          {savedProps === null ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : savedProps.length === 0 ? (
            <div data-testid="saved-properties-empty" className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center">
              <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3"><Heart className="h-6 w-6 text-slate-400" /></div>
              <p className="text-slate-600 font-medium">No saved properties yet</p>
              <p className="text-sm text-slate-400 mt-1 mb-4">Tap the heart on any listing to save it here.</p>
              <Button data-testid="saved-props-browse" onClick={() => nav("/marketplace")} className="h-10 px-5 rounded-xl bg-accent text-accent-foreground font-semibold"><Search className="h-4 w-4 mr-1" /> Browse Marketplace</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedProps.map((sp) => {
                if (!sp.available) {
                  return (
                    <div key={sp.id} data-testid={`saved-prop-${sp.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col">
                      <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center mb-3"><Building2 className="h-5 w-5 text-slate-400" /></div>
                      <p className="text-sm text-slate-600 font-medium">{sp.message}</p>
                      <Button data-testid={`saved-prop-remove-${sp.id}`} variant="outline" onClick={() => unsaveProperty(sp.id)} className="mt-4 h-9 rounded-lg text-sm self-start"><Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove</Button>
                    </div>
                  );
                }
                const rent = sp.listing_type === "For Rent";
                return (
                  <div key={sp.id} data-testid={`saved-prop-${sp.id}`} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <Link to={`/listing/${sp.id}`} data-testid={`saved-prop-open-${sp.id}`} className="block aspect-[4/3] bg-slate-100 relative">
                      <img src={sp.image} alt={sp.title} className="w-full h-full object-cover" />
                      <span className={`absolute top-3 left-3 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${rent ? "bg-[#0F2C59] text-white" : "bg-[#C79A3B] text-white"}`}>{rent ? "Rent" : "Buy"}</span>
                    </Link>
                    <div className="p-4">
                      <div className="font-display text-lg font-bold text-slate-900">{priceLabel(sp)}</div>
                      <h3 className="text-sm font-semibold text-slate-800 mt-0.5 line-clamp-1">{sp.title}</h3>
                      <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" /> {sp.city}</p>
                      <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                        <Link to={`/listing/${sp.id}`} className="flex-1"><Button data-testid={`saved-prop-view-${sp.id}`} className="w-full h-9 rounded-lg text-sm bg-accent text-accent-foreground font-semibold">Open listing</Button></Link>
                        <Button data-testid={`saved-prop-remove-${sp.id}`} variant="outline" onClick={() => unsaveProperty(sp.id)} className="h-9 rounded-lg text-sm px-3"><Heart className="h-3.5 w-3.5 fill-current text-[#C79A3B]" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Saved Searches */}
        <div className="mt-14" data-testid="saved-searches-section">
          <div className="mb-5">
            <h2 className="font-display text-2xl font-bold text-slate-900">Saved Searches</h2>
            <p className="text-sm text-slate-500 mt-1">Re-run a marketplace search you saved.</p>
          </div>
          {savedSearches === null ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : savedSearches.length === 0 ? (
            <div data-testid="saved-searches-empty" className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center">
              <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3"><Bookmark className="h-6 w-6 text-slate-400" /></div>
              <p className="text-slate-600 font-medium">No saved searches yet</p>
              <p className="text-sm text-slate-400 mt-1 mb-4">Apply filters in the marketplace, then tap “Save this search”.</p>
              <Button data-testid="saved-searches-browse" onClick={() => nav("/marketplace")} className="h-10 px-5 rounded-xl bg-accent text-accent-foreground font-semibold"><Search className="h-4 w-4 mr-1" /> Browse Marketplace</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedSearches.map((s) => {
                const rent = s.filters?.listing_type === "For Rent";
                return (
                  <div key={s.id} data-testid={`saved-search-${s.id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
                    <div className="flex items-center justify-between">
                      <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${rent ? "bg-[#0F2C59] text-white" : "bg-[#C79A3B] text-white"}`}>{rent ? "Rent" : "Buy"}</span>
                    </div>
                    <h3 className="font-semibold text-slate-900 mt-3 line-clamp-2" data-testid={`saved-search-name-${s.id}`}>{s.name}</h3>
                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.filters?.city || "Any location"}</p>
                    <p className="text-xs text-slate-600 mt-1">{searchSummary(s.filters || {})}</p>
                    <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                      <Button data-testid={`saved-search-run-${s.id}`} onClick={() => runSearch(s.filters || {})} className="flex-1 h-9 rounded-lg text-sm bg-accent text-accent-foreground font-semibold"><Search className="h-3.5 w-3.5 mr-1.5" /> Run Search</Button>
                      <Button data-testid={`saved-search-delete-${s.id}`} variant="outline" onClick={() => deleteSearch(s.id)} className="h-9 rounded-lg text-sm px-3"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* My Inquiries */}
        <div className="mt-14" data-testid="my-inquiries-section">
          <div className="mb-5">
            <h2 className="font-display text-2xl font-bold text-slate-900">My Inquiries</h2>
            <p className="text-sm text-slate-500 mt-1">Messages and viewing requests you sent to professionals.</p>
          </div>
          {inquiries === null ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : inquiries.length === 0 ? (
            <div data-testid="my-inquiries-empty" className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center">
              <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3"><ClipboardList className="h-6 w-6 text-slate-400" /></div>
              <p className="text-slate-600 font-medium">No inquiries yet</p>
              <p className="text-sm text-slate-400 mt-1 mb-4">Open a listing and send an inquiry or request a viewing.</p>
              <Button data-testid="inquiries-browse" onClick={() => nav("/marketplace")} className="h-10 px-5 rounded-xl bg-accent text-accent-foreground font-semibold"><Search className="h-4 w-4 mr-1" /> Browse Marketplace</Button>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
              {inquiries.map((i) => (
                <Link key={i.id} to={i.listing_id ? `/listing/${i.listing_id}` : (i.profile_path || "#")} data-testid={`my-inquiry-${i.id}`} className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{i.listing_title || (i.professional_name ? `Inquiry to ${i.professional_name}` : "Listing")}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {INQ_LABEL[i.inquiry_type] || i.inquiry_type}
                      {i.listing_city ? ` · ${i.listing_city}` : ""}
                      {i.inquiry_type === "VIEWING_REQUEST" && i.preferred_date ? ` · ${i.preferred_date}${i.preferred_time ? " " + i.preferred_time : ""}` : ""}
                      {i.inquiry_type === "AVAILABILITY_REQUEST" && i.check_in ? ` · ${i.check_in} → ${i.check_out}${i.guests ? " · " + i.guests + " guest(s)" : ""}` : ""}
                    </p>
                  </div>
                  <span className="text-xs text-slate-400 hidden sm:block">{(i.created_at || "").slice(0, 10)}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${INQ_STATUS[i.status] || "bg-slate-100 text-slate-500"}`}>{i.status}</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* My Selling Properties */}
        <div className="mt-14" data-testid="my-selling-section">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-900">My Selling Properties</h2>
              <p className="text-sm text-slate-500 mt-1">Properties you submitted for review and professional representation.</p>
            </div>
            <Button data-testid="sell-new-btn" onClick={() => nav("/sell/new")} className="h-10 px-4 rounded-xl bg-[#0F2C59] hover:bg-[#0F2C59]/90 text-white font-semibold shrink-0"><Plus className="h-4 w-4 mr-1" /> List a property</Button>
          </div>
          {submissions === null ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : submissions.length === 0 ? (
            <div data-testid="my-selling-empty" className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center">
              <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3"><Building2 className="h-6 w-6 text-slate-400" /></div>
              <p className="text-slate-600 font-medium">No properties submitted yet</p>
              <p className="text-sm text-slate-400 mt-1 mb-4">Submit a property to have it reviewed and professionally represented.</p>
              <Button data-testid="my-selling-cta" onClick={() => nav("/sell")} className="h-10 px-5 rounded-xl bg-accent text-accent-foreground font-semibold">Learn about selling</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {submissions.map((s) => (
                <div key={s.id} data-testid={`submission-${s.id}`} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                  <div className="aspect-[4/3] bg-slate-100 relative">
                    {s.image ? <img src={s.image} alt={s.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Building2 className="h-8 w-8 text-slate-300" /></div>}
                    <span data-testid={`submission-state-${s.id}`} className={`absolute top-3 left-3 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${SUB_STATUS[s.review_state] || "bg-slate-100 text-slate-500"}`}>{SUB_LABEL[s.review_state] || s.review_state}</span>
                  </div>
                  <div className="p-4">
                    <div className="font-display text-lg font-bold text-slate-900">{priceLabel(s)}</div>
                    <h3 className="text-sm font-semibold text-slate-800 mt-0.5 line-clamp-1">{s.title}</h3>
                    <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.city} · {propertyTypeLabel(s.property_type)}</p>
                    {s.review_state === "REJECTED" && s.review_reason && <p className="text-xs text-red-600 mt-2">{s.review_reason}</p>}
                    {s.is_public && (
                      <Link to={`/listing/${s.id}`} className="block mt-3"><Button data-testid={`submission-view-${s.id}`} className="w-full h-9 rounded-lg text-sm bg-accent text-accent-foreground font-semibold">View Listing</Button></Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
      <SiteFooter variant="compact" />
    </div>
  );
}
